/**
 * Pure helpers behind the API Explorer's request forms. They live outside the components so the
 * payload transformations — which operate on a user-editable JSON body and therefore cannot trust
 * its shape — are unit-testable without rendering.
 */
import { GraphQL } from "@rpi/openapi-api";
import { isOrderFileField, ORDER_FILE_FIELDS, OrderFileField } from "../../../utils/orderFiles.util";
import { isArray, isEmptyString, isRecord, isString } from "../../../utils/type.util";
import { PLACEHOLDER } from "./apiExplorerSpec";

type ApiCredential = GraphQL.ListApiCredentialsQuery["listApiCredentials"][number];
type CustomerProduct = GraphQL.ListCustomerProductsQuery["listCustomerProducts"][number];

/** The file urls the Explorer writes into, and reads back out of, each order item's product. */
export type ProductFileUrls = Partial<Record<OrderFileField, string>>;

/**
 * Credentials belong to one environment, and the Explorer sends real requests, so a session offers
 * only the keys for the mode it is in — a production session creates production orders, a sandbox
 * session sandbox ones.
 */
export function selectCredentialsForEnvironment(
  credentials: ApiCredential[] | undefined,
  isSandbox: boolean
): ApiCredential[] | undefined {
  return credentials?.filter((credential) => credential.isSandbox === isSandbox);
}

/** Products are offered in catalog order so the list is stable between renders. */
export function sortProductsForSelect(products?: CustomerProduct[]): CustomerProduct[] | undefined {
  return products?.slice().sort((a, b) => a.openapiProductId - b.openapiProductId);
}

/** The first order item, when the body still holds a list of them. */
function readFirstOrderItem(payload: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!isArray(payload.orderItems)) {
    return undefined;
  }
  const [firstItem] = payload.orderItems;
  return isRecord(firstItem) ? firstItem : undefined;
}

/**
 * Rewrites the first order item's product — the same one the form's fields read from, so extra items
 * a caller added are left as they typed them. Returns the payload untouched when the Data editor no
 * longer holds an order item to write into.
 */
function withFirstOrderItemProduct(
  payload: Record<string, unknown>,
  updateProduct: (product: Record<string, unknown>) => Record<string, unknown>,
  sku?: string
): Record<string, unknown> {
  if (!isArray(payload.orderItems)) {
    return payload;
  }
  const [firstItem, ...rest] = payload.orderItems;
  if (!isRecord(firstItem)) {
    return payload;
  }
  const product = isRecord(firstItem.product) ? firstItem.product : {};
  return {
    ...payload,
    orderItems: [
      {
        ...firstItem,
        ...(sku === undefined ? {} : { sku }),
        product: updateProduct(product),
      },
      ...rest,
    ],
  };
}

/**
 * Reads every file url back out of the first order item, falling back to that file's placeholder
 * token. Every step is guarded: the body is user-editable, so any of it may be missing or the wrong
 * type, and a non-string url would otherwise reach the field as its value.
 */
export function readProductFileUrls(payload: Record<string, unknown>): Record<OrderFileField, string> {
  const product = readFirstOrderItem(payload)?.product;
  const readUrl = (field: OrderFileField): string => {
    const value = isRecord(product) ? product[field] : undefined;
    return isString(value) ? value : PLACEHOLDER[field];
  };
  // Spelled out rather than mapped over ORDER_FILE_FIELDS so a fourth file fails to compile here
  // instead of reading as absent at runtime.
  return { coverUrl: readUrl("coverUrl"), dustJacketUrl: readUrl("dustJacketUrl"), gutsUrl: readUrl("gutsUrl") };
}

/**
 * The file fields the body carries that the selected sku does not declare. Order intake compares the
 * two sets for equality, so a file left behind fails the order as surely as a missing one — whether
 * a caller added it in the Data editor or the product's declared set shrank under a selection that
 * already filled it.
 */
export function undeclaredFileFields(
  payload: Record<string, unknown>,
  declared: readonly OrderFileField[]
): OrderFileField[] {
  const product = readFirstOrderItem(payload)?.product;
  if (!isRecord(product)) {
    return [];
  }
  return ORDER_FILE_FIELDS.filter((field) => product[field] !== undefined && !declared.includes(field));
}

/** Writes the given file urls (and optionally the sku) into the first order item's product. */
export function applyFileUrlsToFirstOrderItem(
  payload: Record<string, unknown>,
  fileUrls: ProductFileUrls,
  sku?: string
): Record<string, unknown> {
  return withFirstOrderItemProduct(payload, (product) => ({ ...product, ...fileUrls }), sku);
}

/**
 * Resets the first order item to the files a newly selected product's SKU declares: each declared
 * file holds its placeholder token, and a file the SKU does not declare is removed. Order intake
 * requires the item to carry exactly the declared set, so the removal is what keeps a previous
 * product's file from riding along beside the new one's.
 */
export function setDeclaredFiles(
  payload: Record<string, unknown>,
  declared: readonly OrderFileField[],
  sku?: string
): Record<string, unknown> {
  return withFirstOrderItemProduct(
    payload,
    (product) => ({
      ...Object.fromEntries(Object.entries(product).filter(([key]) => !isOrderFileField(key))),
      ...Object.fromEntries(declared.map((field) => [field, PLACEHOLDER[field]])),
    }),
    sku
  );
}

function isPlaceholderFileUrl(url: string): boolean {
  return ORDER_FILE_FIELDS.some((field) => url === PLACEHOLDER[field]);
}

/**
 * Narrows derived template URLs to the declared files still holding a placeholder token, so a URL the
 * caller typed survives metadata resolving after they typed it — including on a cache hit, which fires
 * again for a product they have already picked.
 */
export function unsetFileUrls(
  payload: Record<string, unknown>,
  derived: ProductFileUrls,
  declared: readonly OrderFileField[]
): ProductFileUrls {
  const current = readProductFileUrls(payload);
  const unset: ProductFileUrls = {};
  for (const field of declared) {
    const derivedUrl = derived[field];
    if (current[field] === PLACEHOLDER[field] && derivedUrl) {
      unset[field] = derivedUrl;
    }
  }
  return unset;
}

/**
 * The URL a file field should show, or an empty string when there is nothing to show.
 *
 * The Data body carries placeholder tokens until a product's template metadata resolves. A token is
 * not a URL, so it reads as an empty field showing its placeholder — the state the design draws —
 * rather than as text the caller has to clear before typing.
 */
export function editableFileUrl(url: string): string {
  return isPlaceholderFileUrl(url) ? "" : url;
}

/**
 * Whether a real product has been picked. The body carries the seed's `{sku}` token until one is, and
 * a token is not a product — so Submit stays disabled rather than posting a placeholder order.
 */
export function isSelectedSku(sku: string): boolean {
  return !isEmptyString(sku) && sku !== PLACEHOLDER.sku;
}
